import * as React from 'react';
import { getBaseUrl } from '@/redux/modules/config';
import { getFirstSearchItemId } from '@/redux/modules/search/search.selectors';
import { getItemCardImageUrl, isSimilarItemsPage, logoUrl } from '@/utils/urls';
import {
    getItemSummaryCatalogId,
    getItemSummaryImageVersion,
    getItemSummaryPhotos,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import isEqual from 'react-fast-compare';
import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';

type Props = {
    page: number;
    searchTerm: string;
};

const SearchPageHeaderMeta = ({ page, searchTerm }: Props) => {
    const { pathname } = useLocation();
    const similarItemsPage = isSimilarItemsPage(pathname);

    const itemId = useAppSelector(getFirstSearchItemId);
    const baseUrl = useAppSelector(getBaseUrl);

    const { formatMessage } = useIntl();
    let title = formatMessage(
        { id: similarItemsPage ? 'moreLikeResults' : 'searchTerms' },
        { searchTerm: cleanSearchTerms(searchTerm) }
    );

    if (page > 1) {
        title += formatMessage({ id: 'page_number_and_title' }, { page });
    }

    title = useAddNotificationCountToTitle(title);

    const description = formatMessage({ id: 'searchTermsDescription' }, { terms: cleanSearchTerms(searchTerm, true) });
    const canonical = `${baseUrl}/search`;

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const imageVersion = useAppSelector((state) => getItemSummaryImageVersion(state, itemId));
    const photos = useAppSelector((state) => getItemSummaryPhotos(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const firstPhoto = photos?.[0] || 0;
    const imageUrl = getItemCardImageUrl(sellerId, catalogId, itemId, firstPhoto, imageVersion) || logoUrl;

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={description}
            imageUrl={imageUrl}
            title={title}
        >
            <meta
                content="noindex, nofollow"
                name="robots"
            />
        </PageHeaderMeta>
    );
};

export default React.memo<Props>(SearchPageHeaderMeta, isEqual);
